import { useFacet } from "@nosto/preact"
import { useState, useEffect, useMemo, useRef } from "preact/hooks"

import { sortData } from "../helpers"
import { sizeFilterOrder, colorHexes, filterValueMapping } from "../config"

export default function Facet({ facet, activeDropdown, handleDropdownChange }) {
    const { selectedFiltersCount, toggleProductFilter } = useFacet(facet)
    const [ active, setActive ] = useState(false);
    const isColor = useMemo(() => facet.name.toLowerCase().includes('color'), [])
    const facetLabel = useMemo(() => `${facet.id}-${handleDropdownChange ? 'desktop' : 'mobile'}`,[])
    const isOpen = useMemo(() => handleDropdownChange ? activeDropdown === facet.id : active, [handleDropdownChange, activeDropdown, facet.id, active]);
    const data = useMemo(() => facet.name.toLowerCase().includes('size') ? sortData(facet.data, sizeFilterOrder, (v) => v.value) : facet.data, [facet.name, facet.data]);
    
    const toggleActive = () => setActive(prevActive => !prevActive);

    const toggleDropdown = () => {
        if(handleDropdownChange) {
            handleDropdownChange(facet.id);
        } else {
            toggleActive();
        }
    }

    /* ----- accessibility ----- */
    const [ focusIndex, setFocusIndex ] = useState(0);
    const listItemRefs = useRef([]);
    const toggleRef = useRef(null);

    useEffect(() => {
        if (isOpen) {
            const selectedElement = listItemRefs.current[focusIndex];
            if (selectedElement) {
                setTimeout(() => {
                    selectedElement.focus();
                }, 50)
            }
        }
    }, [ isOpen, focusIndex ]);

    useEffect(() => { // keyboard controls
        const handleKeydown = (event) => {
            switch(event.key) {
                case "Escape":
                    toggleDropdown();
                    toggleRef.current?.focus();
                    break;
                case "ArrowUp":
                    event.preventDefault();
                    setFocusIndex(prevFocusIndex => (prevFocusIndex > 0) ? prevFocusIndex - 1 : data.length - 1)
                    break;
                case "ArrowDown":
                    event.preventDefault();
                    setFocusIndex(prevFocusIndex => (prevFocusIndex < (data.length - 1)) ? prevFocusIndex + 1 : 0)
                    break;
                default:
                    break;
            }
        }
        if(isOpen && listItemRefs.current?.length) {
            window.document.addEventListener('keydown', handleKeydown)
            return () => window.document.removeEventListener('keydown', handleKeydown)
        }
    }, [ isOpen, focusIndex, data ])

    return (
        <li class={`ns-facet ${isColor ? 'ns-color-facet' : ''}`}>
            <div class="ns-facet-header">
                <button
                    type="button"
                    class="ns-facet-toggle ns-w-100 ns-flex ns-flex-nowrap ns-items-center ns-justify-between"
                    data-nosto-element="facet"
                    aria-controls={`filters-${facetLabel}`}
                    aria-label={`${isOpen ? 'Collapse' : 'Expand' } ${facet.name} filters`}
                    aria-expanded={isOpen}
                    onClick={toggleDropdown}
                    ref={toggleRef}
                    tabIndex="0"
                >
                    <h3 class="ns-folio-reg ns-flex ns-items-center" id={`filters-heading-${facetLabel}`}>
                        {facet.name} {selectedFiltersCount > 0 ? `(${selectedFiltersCount})` : ''}
                    </h3>
                    <span class="ns-toggle-icon ns-relative" role="presentation" aria-hidden="true" />
                </button>
            </div>
            <div 
                class="ns-facet-body" 
                id={`filters-${facetLabel}`}
                aria-labelledby={`filters-heading-${facetLabel}`}
                aria-hidden={!isOpen}
            >
                <ul 
                    class="ns-filters-list ns-list-unstyled ns-flex ns-flex-col" 
                    role="menu"
                >
                    {data?.map((value, i) => {
                        const val = filterValueMapping[facet.name]?.[value.value] || value.value;
                        return (
                            <li
                                key={value.value}
                                class="ns-filter-item ns-folio-reg ns-flex ns-justify-between ns-items-center"
                                data-nosto-element="facet-setting"
                                role="menuitem"
                            >
                                <label 
                                    className='ns-checkbox ns-w-100' 
                                    ref={el => listItemRefs.current[i] = el}
                                    tabIndex={focusIndex === i ? 0 : -1}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            toggleProductFilter(facet.field, value.value, !value.selected);
                                        }
                                    }}
                                >
                                    <input 
                                        class="sr-only visually-hidden" 
                                        type="checkbox" 
                                        name={`ns-filer-${facet.name.toLowerCase().replaceAll(' ', '-')}`}
                                        tabIndex={-1}
                                        checked={value.selected} 
                                        onChange={e => {
                                            e.preventDefault()
                                            toggleProductFilter(facet.field, value.value, !value.selected)
                                        }}
                                    />
                                    <span class="ns-checkbox-label ns-folio-reg">
                                        {val}
                                    </span>
                                    {isColor && 
                                        <span 
                                            class="ns-color-hex"
                                            style={`background: ${colorHexes[val]}; border-color: ${val.toLowerCase() === 'white' ? 'rgba(0, 0, 0, 0.15)' : colorHexes[val]}`}
                                        />
                                    }
                                </label>
                            </li>
                        )
                    })}
                </ul>
            </div>
        </li>
    )
}
